import { useState, useEffect } from 'react';
import { Zap, ArrowLeft, Plus, UserPlus, TrendingUp, Clock, CheckCircle2, Edit, Trash2, Save, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRulesEngine, DbNotificationRule } from '@/lib/useRulesEngine';
import { useSettings } from '@/lib/useSettings';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/lib/toast';
import { slaApi } from '@/api/slaApi';

export function AutomationPage({ 
  onBack, 
  initialTab = 'assignment',
}: { 
  onBack: () => void, 
  initialTab?: string,
}) {
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const { rules, loading: rulesLoading, saveRule, deleteRule } = useRulesEngine();
  const { emailTemplates } = useSettings();
  const { user } = useAuth();
  const { push: toast } = useToast();

  const [slaPolicies, setSlaPolicies] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<Partial<DbNotificationRule> | null>(null);

  // SLA Modal State
  const [isSlaModalOpen, setIsSlaModalOpen] = useState(false);
  const [editingSla, setEditingSla] = useState<any>(null);

  const loadDatabaseRecords = async () => {
    try {
      setLoadingData(true);
      try {
        const sla = await slaApi.getSlaPolicies(user?.role === 'Super Admin' ? 'ALL' : (user?.organizationId || undefined));
        if (sla && Array.isArray(sla)) {
          setSlaPolicies(sla);
          return;
        }
      } catch (err) {
        // Fallback to supabase
      }

      if (supabase) {
        const { data: sla } = await supabase
          .from('sla_policies')
          .select('*')
          .order('created_at', { ascending: true });
        if (sla) setSlaPolicies(sla);
      }
    } catch (e) {
      console.error('Error loading automation data:', e);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    loadDatabaseRecords();
  }, []);

  const handleEdit = (rule?: DbNotificationRule) => {
    setEditingRule(rule ? { ...rule } : { id: `rule-${Math.random()}`, name: '', event_trigger: 'ticket_created', is_active: true });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!editingRule) return;
    await saveRule(editingRule);
    setIsModalOpen(false);
    toast('Rule saved successfully');
  };

  const handleEditSla = (policy?: any) => {
    setEditingSla(policy ? { ...policy } : {
      name: '',
      target_priority: 'Medium',
      first_response_hours: 4,
      resolution_hours: 24,
      is_active: true
    });
    setIsSlaModalOpen(true);
  };

  const handleSaveSla = async () => {
    if (!editingSla?.name?.trim()) return;
    try {
      const orgId = user?.organizationId;
      const payload = {
        organization_id: orgId,
        name: editingSla.name,
        target_priority: editingSla.target_priority,
        first_response_hours: parseFloat(editingSla.first_response_hours) || 4,
        resolution_hours: parseFloat(editingSla.resolution_hours) || 24,
        is_active: editingSla.is_active ?? true
      };

      try {
        if (editingSla.id) {
          await slaApi.updateSlaPolicy(editingSla.id, payload);
        } else {
          await slaApi.createSlaPolicy(payload);
        }
        setIsSlaModalOpen(false);
        await loadDatabaseRecords();
        toast('SLA Policy saved successfully');
        return;
      } catch (apiErr) {
        // Fallback to supabase
        if (!supabase) throw apiErr;
      }

      if (editingSla.id) {
        const { error } = await supabase
          .from('sla_policies')
          .update({
            name: editingSla.name,
            target_priority: editingSla.target_priority,
            first_response_hours: parseFloat(editingSla.first_response_hours) || 4,
            resolution_hours: parseFloat(editingSla.resolution_hours) || 24,
            is_active: editingSla.is_active
          })
          .eq('id', editingSla.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('sla_policies')
          .insert({
            organization_id: orgId,
            name: editingSla.name,
            target_priority: editingSla.target_priority,
            first_response_hours: parseFloat(editingSla.first_response_hours) || 4,
            resolution_hours: parseFloat(editingSla.resolution_hours) || 24,
            is_active: editingSla.is_active ?? true
          });
        if (error) throw error;
      }
      setIsSlaModalOpen(false);
      await loadDatabaseRecords();
      toast('SLA Policy saved successfully');
    } catch (e: any) {
      toast('Failed to save SLA policy: ' + e.message);
    }
  };

  const handleDeleteSla = async (id: string) => {
    try {
      try {
        await slaApi.deleteSlaPolicy(id);
        await loadDatabaseRecords();
        toast('SLA Policy deleted');
        return;
      } catch (apiErr) {
        if (!supabase) throw apiErr;
      }

      const { error } = await supabase.from('sla_policies').delete().eq('id', id);
      if (error) throw error;
      await loadDatabaseRecords();
      toast('SLA Policy deleted');
    } catch (e: any) {
      toast('Failed to delete SLA: ' + e.message);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border bg-card/60 px-6 py-4">
        <button
          onClick={onBack}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground mr-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-500/10 text-yellow-500">
          <Zap className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-lg font-semibold tracking-tight">Automation Engine</h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Configure system-wide rules, escalations, and workflows to reduce manual effort.
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-muted/10 p-4 sm:p-6 lg:p-8">
        <div className="mx-auto w-full max-w-5xl space-y-6">
          
          <div className="flex flex-wrap items-center gap-2 border-b border-border pb-2">
            {[
              { id: 'assignment', label: 'Auto Assignment', icon: UserPlus },
              { id: 'escalation', label: 'Escalation Rules', icon: TrendingUp },
              { id: 'sla', label: 'SLA Policies', icon: Clock },
              { id: 'approvals', label: 'Approval Workflows', icon: CheckCircle2 },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition",
                  activeTab === tab.id ? "bg-card shadow-sm border border-border text-foreground" : "text-muted-foreground hover:bg-muted"
                )}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="rounded-xl border border-border bg-card shadow-sm">
            <div className="border-b border-border px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-display text-base font-medium">
                  {activeTab === 'assignment' ? 'Notification & Assignment Rules' : 
                   activeTab === 'escalation' ? 'Escalation Paths & Automations' :
                   activeTab === 'sla' ? 'SLA Enforcements (Database Policies)' : 'Approval Chains'}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">Manage rules that run automatically when conditions are met.</p>
              </div>
              {activeTab === 'sla' ? (
                <button onClick={() => handleEditSla()} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90">
                  <Plus className="h-4 w-4" /> Add SLA Policy
                </button>
              ) : (
                <button onClick={() => handleEdit()} className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90">
                  <Plus className="h-4 w-4" /> Create Rule
                </button>
              )}
            </div>
            <div className="p-0">
              {(activeTab === 'sla' ? loadingData : rulesLoading) ? (
                <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-muted-foreground h-6 w-6" /></div>
              ) : activeTab === 'sla' ? (
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-5 py-3 font-medium">Policy Name</th>
                      <th className="px-5 py-3 font-medium">Target Priority</th>
                      <th className="px-5 py-3 font-medium">Response Target</th>
                      <th className="px-5 py-3 font-medium">Resolution Target</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {slaPolicies.map(p => (
                      <tr key={p.id} className="transition hover:bg-muted/30">
                        <td className="px-5 py-4 font-medium text-foreground">{p.name}</td>
                        <td className="px-5 py-4">
                          <span className={cn(
                            "rounded px-2 py-0.5 text-xs font-semibold uppercase",
                            p.target_priority === 'Urgent' ? "bg-red-500/10 text-red-500" :
                            p.target_priority === 'High' ? "bg-orange-500/10 text-orange-500" :
                            p.target_priority === 'Medium' ? "bg-blue-500/10 text-blue-500" : "bg-muted text-muted-foreground"
                          )}>
                            {p.target_priority}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-muted-foreground">{p.first_response_hours}h</td>
                        <td className="px-5 py-4 text-muted-foreground">{p.resolution_hours}h</td>
                        <td className="px-5 py-4">
                          <span className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
                            p.is_active ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground"
                          )}>
                            {p.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button onClick={() => handleEditSla(p)} className="p-1.5 text-muted-foreground hover:text-primary transition" title="Edit">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDeleteSla(p.id)} className="p-1.5 text-muted-foreground hover:text-red-500 transition ml-1" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-5 py-3 font-medium">Rule Name</th>
                      <th className="px-5 py-3 font-medium">Trigger Condition</th>
                      <th className="px-5 py-3 font-medium">Action</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rules.map(rule => (
                      <tr key={rule.id} className="transition hover:bg-muted/30">
                        <td className="px-5 py-4 font-medium text-foreground">{rule.name}</td>
                        <td className="px-5 py-4"><span className="rounded bg-muted px-2 py-1 text-xs font-mono">{rule.event_trigger}</span></td>
                        <td className="px-5 py-4 text-muted-foreground">{JSON.stringify(rule.conditions || {})}</td>
                        <td className="px-5 py-4">
                          <span className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
                            rule.is_active ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground"
                          )}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button onClick={() => handleEdit(rule)} className="p-1.5 text-muted-foreground hover:text-primary transition" title="Edit">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button onClick={() => deleteRule(rule.id)} className="p-1.5 text-muted-foreground hover:text-red-500 transition ml-1" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl rounded-xl border border-border bg-card p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between border-b border-border pb-4">
              <h2 className="font-display text-lg font-semibold">Edit Rule</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Rule Name</label>
                <input value={editingRule?.name || ''} onChange={e => setEditingRule({...editingRule, name: e.target.value})} placeholder="e.g., Auto-assign critical security tickets" className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" />
              </div>
              
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">When (Trigger)</label>
                  <select value={editingRule?.event_trigger || 'ticket_created'} onChange={e => setEditingRule({...editingRule, event_trigger: e.target.value})} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary">
                    <option value="ticket_created">Ticket Created</option>
                    <option value="ticket_assigned">Ticket Assigned</option>
                    <option value="ticket_accepted">Ticket Accepted</option>
                    <option value="ticket_status_changed">Ticket Status Changed</option>
                    <option value="technician_comment">Technician Comment Added</option>
                    <option value="requester_comment">Requester Reply</option>
                    <option value="approval_required">Approval Required</option>
                    <option value="ticket_escalated">Ticket Escalation</option>
                    <option value="ticket_resolved">Ticket Resolved</option>
                    <option value="ticket_closed">Ticket Closed</option>
                    <option value="ticket_reopened">Ticket Reopened</option>
                    <option value="ticket_cancelled">Ticket Cancelled</option>
                  </select>
                </div>
                
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">If (Condition)</label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select className="w-full sm:w-1/3 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary">
                      <option>Priority</option>
                      <option>Category</option>
                      <option>Reporter</option>
                      <option>Status</option>
                    </select>
                    <select className="w-full sm:w-1/4 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary">
                      <option>Equals</option>
                      <option>Contains</option>
                      <option>Is Not</option>
                    </select>
                    <input placeholder="Value" className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" />
                  </div>
                  <button className="mt-2 text-[11px] font-medium text-primary hover:underline flex items-center gap-1"><Plus className="h-3 w-3"/> Add Condition</button>
                </div>
              </div>


              <div className="rounded-lg border border-border bg-primary/5 p-4 space-y-4">
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Then (Action)</label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select value={editingRule?.action?.split(' ')[0] || 'Assign'} onChange={e => {
                        const newAction = e.target.value;
                        setEditingRule({...editingRule, action: newAction === 'Email' ? 'Send Email' : newAction});
                    }} className="w-full sm:w-1/3 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary">
                      <option value="Assign">Assign to</option>
                      <option value="Set">Set Field</option>
                      <option value="Email">Send Email</option>
                      <option value="Escalate">Escalate</option>
                    </select>
                    
                    {editingRule?.action?.includes('Email') ? (
                       <select value={editingRule?.template_id || ''} onChange={e => setEditingRule({...editingRule, template_id: e.target.value})} className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary">
                          <option value="">Select an Email Template...</option>
                          {emailTemplates.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                       </select>
                    ) : (
                      <input placeholder="Action Value (e.g. Security Team, Priority High)" value={editingRule?.action?.split(' ').slice(1).join(' ') || ''} onChange={e => {
                          const actionType = editingRule?.action?.split(' ')[0] || 'Assign';
                          setEditingRule({...editingRule, action: actionType + ' ' + e.target.value});
                      }} className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" />
                    )}
                  </div>
                  <button className="mt-2 text-[11px] font-medium text-primary hover:underline flex items-center gap-1"><Plus className="h-3 w-3"/> Add Action</button>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
                <select value={editingRule?.is_active ? 'Active' : 'Inactive'} onChange={e => setEditingRule({...editingRule, is_active: e.target.value === 'Active'})} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary">
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-border mt-6">
                <button onClick={() => setIsModalOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition">Cancel</button>
                <button onClick={handleSave} className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"><Save className="h-4 w-4" /> Save Rule</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSlaModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-xl border border-border bg-card p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between border-b border-border pb-4">
              <h2 className="font-display text-lg font-semibold">{editingSla?.id ? 'Edit SLA Policy' : 'Create SLA Policy'}</h2>
              <button onClick={() => setIsSlaModalOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Policy Name</label>
                <input 
                  value={editingSla?.name || ''} 
                  onChange={e => setEditingSla({ ...editingSla, name: e.target.value })} 
                  placeholder="e.g. Critical Incident SLA" 
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" 
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Target Priority</label>
                <select 
                  value={editingSla?.target_priority || 'Medium'} 
                  onChange={e => setEditingSla({ ...editingSla, target_priority: e.target.value })} 
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                >
                  <option value="Urgent">Urgent (P1)</option>
                  <option value="High">High (P2)</option>
                  <option value="Medium">Medium (P3)</option>
                  <option value="Low">Low (P4)</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">First Response Target (Hours)</label>
                  <input 
                    type="number"
                    step="0.25"
                    value={editingSla?.first_response_hours ?? 4} 
                    onChange={e => setEditingSla({ ...editingSla, first_response_hours: e.target.value })} 
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" 
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Resolution Target (Hours)</label>
                  <input 
                    type="number"
                    step="0.5"
                    value={editingSla?.resolution_hours ?? 24} 
                    onChange={e => setEditingSla({ ...editingSla, resolution_hours: e.target.value })} 
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" 
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
                <select 
                  value={editingSla?.is_active ? 'Active' : 'Inactive'} 
                  onChange={e => setEditingSla({ ...editingSla, is_active: e.target.value === 'Active' })} 
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-border mt-6">
                <button onClick={() => setIsSlaModalOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition">Cancel</button>
                <button onClick={handleSaveSla} className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90">
                  <Save className="h-4 w-4" /> Save SLA Policy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
